import { useEffect, useMemo, useRef, useState } from "react";
import { KeyRound } from "lucide-react";
import { useServersStore, useVaultStore } from "../store";

type VaultKind = "secret" | "config";

export function VaultReferencePicker({
  onInsert,
  serverId,
  preferredKind = "secret",
}: {
  onInsert: (template: string) => void;
  serverId?: string | undefined;
  preferredKind?: VaultKind;
}) {
  const entries = useVaultStore((state) => state.entries);
  const servers = useServersStore((state) => state.servers);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<VaultKind>(preferredKind);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setKind(preferredKind);
  }, [preferredKind]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const visibleEntries = useMemo(() => {
    return entries
      .filter((entry) => entry.kind === kind)
      .filter((entry) => entry.scopeType === "global" || (serverId && entry.scopeServerId === serverId))
      .sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: "base" }));
  }, [entries, kind, serverId]);

  const disabled = visibleEntries.length === 0;

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        className="inline-flex h-8 w-8 items-center justify-center rounded border border-border bg-surface text-zinc-500 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
        title={
          visibleEntries.length === 0
            ? "No matching vault entries available"
            : "Insert vault reference"
        }
      >
        <KeyRound size={13} />
      </button>

      {open && !disabled && (
        <div className="absolute right-0 top-full z-20 mt-1 w-72 rounded-lg border border-border bg-surface-raised p-2 shadow-xl">
          <div className="mb-2 flex gap-1">
            <button
              type="button"
              onClick={() => setKind("secret")}
              className={`rounded px-2 py-1 text-[11px] font-medium ${
                kind === "secret" ? "bg-brand text-zinc-900" : "bg-surface text-zinc-500 ring-1 ring-border hover:text-zinc-200"
              }`}
            >
              Secret
            </button>
            <button
              type="button"
              onClick={() => setKind("config")}
              className={`rounded px-2 py-1 text-[11px] font-medium ${
                kind === "config" ? "bg-brand text-zinc-900" : "bg-surface text-zinc-500 ring-1 ring-border hover:text-zinc-200"
              }`}
            >
              Config
            </button>
          </div>

          <div className="max-h-56 space-y-1 overflow-y-auto">
            {visibleEntries.length === 0 ? (
              <div className="rounded px-2 py-2 text-[11px] text-zinc-500">No matching entries</div>
            ) : (
              visibleEntries.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => {
                    onInsert(`{{${kind}:${entry.key}}}`);
                    setOpen(false);
                  }}
                  className="flex w-full items-start justify-between gap-2 rounded px-2 py-1.5 text-left hover:bg-surface"
                >
                  <span className="min-w-0 font-mono text-xs text-zinc-200">{entry.key}</span>
                  <span className="shrink-0 text-[10px] text-zinc-500">
                    {entry.scopeType === "global"
                      ? "Global"
                      : servers.find((server) => server.id === entry.scopeServerId)?.name ?? "Server"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
